import {
  useEffect, useRef, useState, type RefObject,
} from 'react';
import type { MyIpListReturnState } from '$/domains/myIp/returnState';
// 仅订阅 Next 的路径变化，不绕过项目导航代理。
// eslint-disable-next-line ban/ban
import { useRouter } from 'next/router';
import { getMyIpDetailHref } from '@/utils/myIp/routes';
import {
  MY_IP_RETURN_QUERY, MY_IP_RESTORE_QUERY, rememberMyIpReturnSource, resolveMyIpReturnHref, consumeMyIpReturnState,
} from '@/utils/myIp/backNavigation';

/** 客户端记录当前列表或资产库宿主 URL；SSR 和存储受限时仍提供可用详情链接。 */
export function useMyIpDetailLinks(scopeKey: string, listPath: string) {
  // eslint-disable-next-line ban/ban
  const { asPath } = useRouter();
  const [source, setSource] = useState<{ scopeKey: string; asPath: string; token?: string; }>();
  useEffect(() => {
    const href = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    setSource({ scopeKey, asPath, token: rememberMyIpReturnSource(href, scopeKey) });
  }, [scopeKey, listPath, asPath]);
  /** 来源 token 只用于安全返回，不能充当详情访问权限或触发任何业务写入。 */
  return (ipId: string) => {
    const href = getMyIpDetailHref(listPath, ipId);
    return source?.scopeKey === scopeKey && source.asPath === asPath && source.token ? `${href}?${MY_IP_RETURN_QUERY}=${source.token}` : href;
  };
}

/** 客户端恢复与当前身份匹配的来源；身份切换当次渲染即隐藏旧账号返回地址。 */
export function useMyIpBackHref(token: unknown, scopeKey: string, fallback: string) {
  const [source, setSource] = useState<{ token: unknown; scopeKey: string; fallback: string; href: string; }>();
  useEffect(() => {
    setSource({
      token, scopeKey, fallback, href: resolveMyIpReturnHref(token, scopeKey, fallback),
    });
  }, [token, scopeKey, fallback]);
  return source && source.token === token && source.scopeKey === scopeKey && source.fallback === fallback ? source.href : fallback;
}

/** 列表返回从真实接口重新补齐页数，成功渲染后恢复滚动；失败停下等待用户重试。 */
export function useMyIpListReturn(
  scopeKey: string,
  enabled: boolean,
  shell: RefObject<HTMLDivElement>,
  state: { status: string; loadedPages?: number; hasMore: boolean; loadingMore: boolean; moreError?: unknown; },
  loadMore: () => Promise<void>,
) {
  // eslint-disable-next-line ban/ban
  const router = useRouter();
  const pending = useRef<{ scopeKey: string; asPath: string; state: MyIpListReturnState; }>();
  const token = router.query?.[MY_IP_RESTORE_QUERY];
  useEffect(() => {
    if (!enabled || !router.isReady) return;
    const source = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const restored = consumeMyIpReturnState(token, scopeKey, source);
    if (restored?.kind === 'list') pending.current = { scopeKey, asPath: router.asPath, state: restored };
  }, [enabled, router.isReady, router.asPath, token, scopeKey]);
  useEffect(() => {
    const target = pending.current;
    if (!target || target.scopeKey !== scopeKey || target.asPath !== router.asPath || !enabled
      || state.status !== 'allowed' || state.loadingMore || state.moreError) return;
    if ((state.loadedPages || 1) < target.state.page && state.hasMore) { loadMore(); return; }
    const container = shell.current;
    if (container) container.scrollTop = target.state.scrollTop;
    pending.current = undefined;
  }, [enabled, state, scopeKey, router.asPath, loadMore, shell]);
}
